import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Users, Plus, Edit, Trash2, DollarSign, Briefcase, Building2,
  UserPlus, CalendarClock, CheckCircle2, ClipboardList, Send, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(parseFloat(v) || 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "—");
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const nomes = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${nomes[parseInt(m, 10) - 1]}/${y}`;
};

const emptyEmp = {
  name: "", cpf: "", rg: "", phone: "", address: "", role: "",
  admission_date: "", employment_type: "mensal", monthly_salary: "", daily_rate: "",
  bank_name: "", bank_agency: "", bank_account: "", pix_key: "",
  default_project_id: "", active: true, notes: "",
};

export default function PayrollTab({ companyId, bankAccounts = [], currentUser = null, selectedCompany = null, onDataChange }) {
  const isAdmin =
    ["owner", "admin"].includes((currentUser?.role || "").toLowerCase()) ||
    (selectedCompany && currentUser && selectedCompany.owner_id === currentUser.id);

  // Data
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [phasesByProject, setPhasesByProject] = useState({});
  const [batches, setBatches] = useState([]);
  const [monthFilter, setMonthFilter] = useState(currentMonth());

  // Employee dialog
  const [empDlg, setEmpDlg] = useState({ open: false, data: { ...emptyEmp } });

  // Generate batch dialog
  const [genDlg, setGenDlg] = useState({
    open: false,
    competency_month: currentMonth(),
    project_id: "",
    payment_due_date: "",
    notes: "",
    selected: {}, // employee_id -> {days_worked, phase_id, benefits, deductions}
  });

  // View batch dialog
  const [viewDlg, setViewDlg] = useState({ open: false, batch: null });

  // Pay batch dialog
  const [payDlg, setPayDlg] = useState({
    open: false, batch: null, bank_account_id: "",
    payment_date: new Date().toISOString().split("T")[0],
  });

  // Approve dialog (requires medição)
  const [approveDlg, setApproveDlg] = useState({ open: false, batch: null, phase_id: "" });

  // Schedule dialog
  const [schedDlg, setSchedDlg] = useState({ open: false, batch: null, date: "" });

  const fetchAll = async () => {
    if (!companyId) return;
    try {
      const [eRes, prRes, bRes] = await Promise.all([
        axios.get(`${API}/companies/${companyId}/employees`),
        axios.get(`${API}/companies/${companyId}/construction/projects`).catch(() => ({ data: [] })),
        axios.get(`${API}/companies/${companyId}/payroll-batches`),
      ]);
      setEmployees(eRes.data || []);
      setProjects(prRes.data || []);
      setBatches(bRes.data || []);
    } catch (e) {
      toast.error("Erro ao carregar dados: " + (e.response?.data?.detail || ""));
    }
  };
  useEffect(() => { fetchAll(); }, [companyId]);

  const loadPhases = async (projectId) => {
    if (!projectId || phasesByProject[projectId]) return;
    try {
      const r = await axios.get(`${API}/companies/${companyId}/construction/projects/${projectId}/schedule`);
      setPhasesByProject((p) => ({ ...p, [projectId]: r.data || [] }));
    } catch (e) { /* silencioso */ }
  };

  // ============ EMPLOYEES ============
  const openNewEmp = () => setEmpDlg({ open: true, data: { ...emptyEmp } });
  const openEditEmp = (emp) => {
    setEmpDlg({
      open: true,
      data: {
        ...emptyEmp,
        ...emp,
        admission_date: emp.admission_date ? String(emp.admission_date).split("T")[0] : "",
        monthly_salary: emp.monthly_salary ?? "",
        daily_rate: emp.daily_rate ?? "",
        default_project_id: emp.default_project_id || "",
      },
    });
  };
  const saveEmp = async () => {
    const d = empDlg.data;
    if (!d.name?.trim()) return toast.error("Nome obrigatório");
    if (!["mensal", "diaria"].includes(d.employment_type)) return toast.error("Tipo inválido");
    if (d.employment_type === "mensal" && !(parseFloat(d.monthly_salary) > 0)) return toast.error("Salário mensal obrigatório");
    if (d.employment_type === "diaria" && !(parseFloat(d.daily_rate) > 0)) return toast.error("Valor da diária obrigatório");
    try {
      const payload = {
        ...d,
        monthly_salary: d.monthly_salary === "" ? null : parseFloat(d.monthly_salary),
        daily_rate: d.daily_rate === "" ? null : parseFloat(d.daily_rate),
        default_project_id: d.default_project_id || null,
        admission_date: d.admission_date || null,
      };
      if (d.id) {
        await axios.put(`${API}/companies/${companyId}/employees/${d.id}`, payload);
      } else {
        await axios.post(`${API}/companies/${companyId}/employees`, payload);
      }
      toast.success("Funcionário salvo");
      setEmpDlg({ open: false, data: { ...emptyEmp } });
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };
  const delEmp = async (emp) => {
    if (!window.confirm(`Excluir/desativar funcionário ${emp.name}?`)) return;
    try {
      const r = await axios.delete(`${API}/companies/${companyId}/employees/${emp.id}`);
      toast.success(r.data?.message || "Excluído");
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  // ============ GENERATE BATCH ============
  const openGen = () => {
    setGenDlg({
      open: true, competency_month: monthFilter, project_id: "", payment_due_date: "",
      notes: "", selected: {},
    });
  };
  const toggleEmpInBatch = (empId, checked) => {
    setGenDlg((g) => {
      const s = { ...g.selected };
      if (checked) {
        s[empId] = { days_worked: "", phase_id: "", benefits: "", deductions: "", override_value: "" };
      } else {
        delete s[empId];
      }
      return { ...g, selected: s };
    });
  };
  const updateEmpBatch = (empId, patch) => {
    setGenDlg((g) => ({ ...g, selected: { ...g.selected, [empId]: { ...g.selected[empId], ...patch } } }));
  };
  const filteredEmployeesForGen = useMemo(() => {
    if (!genDlg.project_id) return employees.filter((e) => e.active !== false);
    // Show all active; hint by default_project_id
    return employees.filter((e) => e.active !== false);
  }, [employees, genDlg.project_id]);

  const genPreviewTotal = useMemo(() => {
    let total = 0;
    Object.entries(genDlg.selected).forEach(([eid, cfg]) => {
      const emp = employees.find((x) => x.id === eid);
      if (!emp) return;
      let base = 0;
      if (cfg.override_value && parseFloat(cfg.override_value) > 0) {
        base = parseFloat(cfg.override_value);
      } else if (emp.employment_type === "diaria") {
        base = (parseFloat(cfg.days_worked) || 0) * (parseFloat(emp.daily_rate) || 0);
      } else {
        base = parseFloat(emp.monthly_salary) || 0;
      }
      const b = parseFloat(cfg.benefits) || 0;
      const d = parseFloat(cfg.deductions) || 0;
      total += Math.max(0, base + b - d);
    });
    return total;
  }, [genDlg.selected, employees]);

  const submitGen = async () => {
    if (!genDlg.project_id) return toast.error("Selecione a obra/local");
    const emps = Object.entries(genDlg.selected).map(([eid, cfg]) => ({
      employee_id: eid,
      days_worked: cfg.days_worked ? parseInt(cfg.days_worked, 10) : null,
      phase_id: cfg.phase_id || null,
      benefits: cfg.benefits === "" ? 0 : parseFloat(cfg.benefits),
      deductions: cfg.deductions === "" ? 0 : parseFloat(cfg.deductions),
      override_value: cfg.override_value === "" ? null : parseFloat(cfg.override_value),
    }));
    if (emps.length === 0) return toast.error("Selecione ao menos um funcionário");
    // Validar dias trabalhados para diaristas
    for (const item of emps) {
      const emp = employees.find((x) => x.id === item.employee_id);
      if (emp?.employment_type === "diaria" && (!item.days_worked || item.days_worked <= 0)) {
        return toast.error(`Informe os dias trabalhados para ${emp.name}`);
      }
    }
    try {
      await axios.post(`${API}/companies/${companyId}/payroll-batches/generate`, {
        competency_month: genDlg.competency_month,
        project_id: genDlg.project_id,
        payment_due_date: genDlg.payment_due_date || null,
        notes: genDlg.notes || null,
        employees: emps,
      });
      toast.success("Folha gerada com sucesso");
      setGenDlg((g) => ({ ...g, open: false }));
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  // ============ BATCH ACTIONS ============
  const openViewBatch = async (batch) => {
    try {
      const r = await axios.get(`${API}/companies/${companyId}/payroll-batches/${batch.id}`);
      setViewDlg({ open: true, batch: r.data });
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };
  const approveBatch = (batch) => {
    if (!isAdmin) return toast.error("Apenas Admin/Owner pode aprovar");
    // pre-load phases for this batch's project (skip for SEDE)
    if (batch.project_id !== "SEDE") loadPhases(batch.project_id);
    setApproveDlg({ open: true, batch, phase_id: "" });
  };
  const confirmApprove = async () => {
    const isSede = approveDlg.batch?.project_id === "SEDE";
    if (!isSede && !approveDlg.phase_id) return toast.error("Selecione a Medição");
    try {
      await axios.post(`${API}/companies/${companyId}/payroll-batches/${approveDlg.batch.id}/approve`, {
        phase_id: isSede ? null : approveDlg.phase_id,
      });
      toast.success(isSede ? "Folha administrativa aprovada" : "Folha aprovada e lançada em Materiais e Serviços");
      setApproveDlg({ open: false, batch: null, phase_id: "" });
      fetchAll();
      if (onDataChange) onDataChange();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };
  const openPayBatch = (batch) => {
    const defaultBank = bankAccounts.find((b) => b.is_default);
    setPayDlg({
      open: true, batch,
      bank_account_id: defaultBank?.id || (bankAccounts[0]?.id || ""),
      payment_date: new Date().toISOString().split("T")[0],
    });
  };
  const confirmPayBatch = async () => {
    if (!payDlg.bank_account_id) return toast.error("Selecione a conta bancária");
    try {
      await axios.post(`${API}/companies/${companyId}/payroll-batches/${payDlg.batch.id}/pay`, {
        bank_account_id: payDlg.bank_account_id,
        payment_date: payDlg.payment_date || null,
      });
      toast.success("Folha paga e conta debitada");
      setPayDlg({ open: false, batch: null, bank_account_id: "", payment_date: "" });
      fetchAll();
      if (onDataChange) onDataChange();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };
  const openSchedule = (batch) => {
    setSchedDlg({
      open: true, batch,
      date: batch.payment_scheduled_date ? String(batch.payment_scheduled_date).split("T")[0] : "",
    });
  };
  const confirmSchedule = async () => {
    if (!schedDlg.date) return toast.error("Selecione a data");
    try {
      await axios.post(`${API}/companies/${companyId}/payroll-batches/${schedDlg.batch.id}/schedule`, {
        payment_scheduled_date: schedDlg.date,
      });
      toast.success("Data de pagamento programada");
      setSchedDlg({ open: false, batch: null, date: "" });
      fetchAll();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };
  const delBatch = async (batch) => {
    const warn = batch.status === "paid"
      ? `Excluir folha PAGA de ${monthLabel(batch.competency_month)}? Isso irá REVERTER o débito da conta ${batch.bank_account_name || ""}.`
      : `Excluir folha de ${monthLabel(batch.competency_month)}?`;
    if (!window.confirm(warn)) return;
    try {
      await axios.delete(`${API}/companies/${companyId}/payroll-batches/${batch.id}`);
      toast.success("Excluído");
      fetchAll();
      if (onDataChange) onDataChange();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  const filteredBatches = useMemo(
    () => (monthFilter ? batches.filter((b) => b.competency_month === monthFilter) : batches),
    [batches, monthFilter]
  );

  const totals = useMemo(() => {
    const t = filteredBatches.reduce((s, b) => s + (parseFloat(b.total_amount) || 0), 0);
    const paid = filteredBatches.filter((b) => b.status === "paid").reduce((s, b) => s + (parseFloat(b.total_amount) || 0), 0);
    const approved = filteredBatches.filter((b) => b.status === "approved").reduce((s, b) => s + (parseFloat(b.total_amount) || 0), 0);
    const pending = filteredBatches.filter((b) => b.status === "pending").reduce((s, b) => s + (parseFloat(b.total_amount) || 0), 0);
    return { total: t, paid, approved, pending };
  }, [filteredBatches]);

  // Programação: sort batches by scheduled or due date, upcoming
  const scheduledBatches = useMemo(() => {
    return batches
      .filter((b) => b.status !== "paid" && (b.payment_scheduled_date || b.payment_due_date))
      .map((b) => ({ ...b, _date: b.payment_scheduled_date || b.payment_due_date }))
      .sort((a, c) => new Date(a._date) - new Date(c._date));
  }, [batches]);

  const statusBadge = (st) => {
    if (st === "paid") return <Badge className="bg-green-600">Pago</Badge>;
    if (st === "approved") return <Badge className="bg-blue-600">Aprovado</Badge>;
    if (st === "cancelled") return <Badge variant="outline" className="border-red-500 text-red-600">Cancelado</Badge>;
    return <Badge variant="outline" className="border-orange-500 text-orange-600">Pendente</Badge>;
  };

  return (
    <div className="space-y-4 payroll-tab" data-testid="payroll-tab">
      <style>{`
        .payroll-tab, .payroll-tab * { color: #000000 !important; }
        .payroll-tab label { font-weight: 700 !important; }
        .payroll-tab .money-out { color: #dc2626 !important; }
        .payroll-tab .money-pending { color: #d97706 !important; }
        .payroll-tab .money-total { color: #1d4ed8 !important; }
        .payroll-tab .money-approved { color: #16a34a !important; }
        .payroll-tab input::placeholder, .payroll-tab textarea::placeholder {
          color: #4b5563 !important; opacity: 1 !important;
        }
        .payroll-tab .badge-pill { padding: 2px 8px; font-size: 11px; font-weight: 700; }
      `}</style>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl md:text-2xl font-extrabold flex items-center gap-2">
          <Users className="h-6 w-6 text-blue-600" /> Folha de Pagamento
        </h2>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-xs">Competência</label>
            <Input type="month" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="w-40" data-testid="payroll-month-filter" />
          </div>
        </div>
      </div>

      <Tabs defaultValue="batches">
        <TabsList>
          <TabsTrigger value="batches" data-testid="payroll-tab-batches"><ClipboardList className="h-4 w-4 mr-1" /> Folhas</TabsTrigger>
          <TabsTrigger value="employees" data-testid="payroll-tab-employees"><Users className="h-4 w-4 mr-1" /> Funcionários</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="payroll-tab-schedule"><CalendarClock className="h-4 w-4 mr-1" /> Programação</TabsTrigger>
        </TabsList>

        {/* ============ FOLHAS ============ */}
        <TabsContent value="batches" className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 flex-1">
              <Card className="bg-blue-50 border-blue-200"><CardContent className="p-3">
                <p className="text-xs font-bold">Total {monthLabel(monthFilter)}</p>
                <p className="text-lg font-extrabold money-total" data-testid="payroll-total-month">{fmt(totals.total)}</p>
              </CardContent></Card>
              <Card className="bg-orange-50 border-orange-200"><CardContent className="p-3">
                <p className="text-xs font-bold">Pendente</p>
                <p className="text-lg font-extrabold money-pending">{fmt(totals.pending)}</p>
              </CardContent></Card>
              <Card className="bg-cyan-50 border-cyan-200"><CardContent className="p-3">
                <p className="text-xs font-bold">Aprovado</p>
                <p className="text-lg font-extrabold money-approved">{fmt(totals.approved)}</p>
              </CardContent></Card>
              <Card className="bg-green-50 border-green-200"><CardContent className="p-3">
                <p className="text-xs font-bold">Pago</p>
                <p className="text-lg font-extrabold money-approved">{fmt(totals.paid)}</p>
              </CardContent></Card>
            </div>
            <Button className="bg-blue-500 hover:bg-blue-600" onClick={openGen} data-testid="payroll-generate-btn">
              <PlayCircle className="h-4 w-4 mr-1" /> Gerar Folha
            </Button>
          </div>

          {filteredBatches.length === 0 ? (
            <Card><CardContent className="p-6 text-center font-bold">Nenhuma folha gerada para {monthLabel(monthFilter)}</CardContent></Card>
          ) : (
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-blue-50">
                    <tr className="border-b">
                      <th className="text-left p-2 font-bold">Competência</th>
                      <th className="text-left p-2 font-bold">Obra/Local</th>
                      <th className="text-right p-2 font-bold">Funcionários</th>
                      <th className="text-right p-2 font-bold">Total</th>
                      <th className="text-left p-2 font-bold">Vencimento / Programado</th>
                      <th className="text-center p-2 font-bold">Status</th>
                      <th className="text-center p-2 font-bold">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBatches.map((b) => (
                      <tr key={b.id} className="border-b hover:bg-blue-50" data-testid={`payroll-batch-row-${b.id}`}>
                        <td className="p-2 font-bold">{monthLabel(b.competency_month)}</td>
                        <td className="p-2 text-xs"><Building2 className="h-3 w-3 inline mr-1" />{b.project_name || "—"}</td>
                        <td className="p-2 text-right">{b.items_count}</td>
                        <td className="p-2 text-right font-extrabold money-out">{fmt(b.total_amount)}</td>
                        <td className="p-2 text-xs">
                          {fmtDate(b.payment_due_date)}
                          {b.payment_scheduled_date && (
                            <div className="text-xs money-total"><CalendarClock className="h-3 w-3 inline" /> {fmtDate(b.payment_scheduled_date)}</div>
                          )}
                        </td>
                        <td className="p-2 text-center">{statusBadge(b.status)}</td>
                        <td className="p-2 text-center">
                          <div className="flex gap-1 justify-center flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => openViewBatch(b)} data-testid={`payroll-view-${b.id}`}>
                              <ClipboardList className="h-3 w-3" />
                            </Button>
                            {b.status === "pending" && isAdmin && (
                              <Button size="sm" className="bg-cyan-500 hover:bg-cyan-600" onClick={() => approveBatch(b)} data-testid={`payroll-approve-${b.id}`}>
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Aprovar
                              </Button>
                            )}
                            {b.status === "approved" && !b.payment_scheduled_date && (
                              <Button size="sm" variant="outline" onClick={() => openSchedule(b)} data-testid={`payroll-schedule-${b.id}`}>
                                <CalendarClock className="h-3 w-3 mr-1" /> Programar
                              </Button>
                            )}
                            {b.status === "approved" && b.payment_scheduled_date && (
                              <Button size="sm" variant="outline" onClick={() => openSchedule(b)} title="Reprogramar" data-testid={`payroll-reschedule-${b.id}`}>
                                <CalendarClock className="h-3 w-3" />
                              </Button>
                            )}
                            {b.status === "approved" && b.payment_scheduled_date && (
                              <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => openPayBatch(b)} data-testid={`payroll-pay-${b.id}`}>
                                <DollarSign className="h-3 w-3 mr-1" /> Pagar
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => delBatch(b)} data-testid={`payroll-delete-${b.id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ============ FUNCIONÁRIOS ============ */}
        <TabsContent value="employees" className="space-y-3">
          <div className="flex items-center justify-end">
            <Button className="bg-blue-500 hover:bg-blue-600" onClick={openNewEmp} data-testid="employee-new-btn">
              <UserPlus className="h-4 w-4 mr-1" /> Novo Funcionário
            </Button>
          </div>
          {employees.length === 0 ? (
            <Card><CardContent className="p-6 text-center font-bold">Nenhum funcionário cadastrado</CardContent></Card>
          ) : (
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-blue-50">
                  <tr className="border-b">
                    <th className="text-left p-2 font-bold">Nome</th>
                    <th className="text-left p-2 font-bold">Cargo</th>
                    <th className="text-left p-2 font-bold">CPF</th>
                    <th className="text-left p-2 font-bold">Tipo</th>
                    <th className="text-right p-2 font-bold">Valor</th>
                    <th className="text-left p-2 font-bold">Local Padrão</th>
                    <th className="text-center p-2 font-bold">Ativo</th>
                    <th className="text-center p-2 font-bold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((e) => (
                    <tr key={e.id} className="border-b hover:bg-blue-50" data-testid={`employee-row-${e.id}`}>
                      <td className="p-2 font-bold">{e.name}</td>
                      <td className="p-2 text-xs">{e.role || "—"}</td>
                      <td className="p-2 text-xs">{e.cpf || "—"}</td>
                      <td className="p-2 text-xs">
                        {e.employment_type === "diaria" ? <Badge variant="outline" className="badge-pill">Diária</Badge> : <Badge className="bg-blue-600 badge-pill">Mensal</Badge>}
                      </td>
                      <td className="p-2 text-right money-total font-bold">
                        {e.employment_type === "diaria" ? `${fmt(e.daily_rate)}/dia` : fmt(e.monthly_salary)}
                      </td>
                      <td className="p-2 text-xs">{e.default_project_name || "—"}</td>
                      <td className="p-2 text-center">
                        {e.active !== false ? <Badge className="bg-green-600 badge-pill">Sim</Badge> : <Badge variant="outline" className="badge-pill">Não</Badge>}
                      </td>
                      <td className="p-2 text-center">
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" variant="outline" onClick={() => openEditEmp(e)} data-testid={`employee-edit-${e.id}`}>
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => delEmp(e)} data-testid={`employee-delete-${e.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          )}
        </TabsContent>

        {/* ============ PROGRAMAÇÃO ============ */}
        <TabsContent value="schedule" className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold">Próximos pagamentos de folha (não pagos, ordenados por data)</p>
            <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="payroll-schedule-print">
              Imprimir
            </Button>
          </div>
          {scheduledBatches.length === 0 ? (
            <Card><CardContent className="p-6 text-center font-bold">Nenhum pagamento programado</CardContent></Card>
          ) : (
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-blue-50">
                  <tr className="border-b">
                    <th className="text-left p-2 font-bold">Data</th>
                    <th className="text-left p-2 font-bold">Competência</th>
                    <th className="text-left p-2 font-bold">Obra/Local</th>
                    <th className="text-right p-2 font-bold">Funcionários</th>
                    <th className="text-right p-2 font-bold">Valor</th>
                    <th className="text-center p-2 font-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledBatches.map((b) => (
                    <tr key={b.id} className="border-b hover:bg-blue-50">
                      <td className="p-2 font-bold money-total">{fmtDate(b._date)}</td>
                      <td className="p-2 text-xs">{monthLabel(b.competency_month)}</td>
                      <td className="p-2 text-xs">{b.project_name || "—"}</td>
                      <td className="p-2 text-right">{b.items_count}</td>
                      <td className="p-2 text-right font-extrabold money-out">{fmt(b.total_amount)}</td>
                      <td className="p-2 text-center">{statusBadge(b.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* ============ DIALOG: EMPLOYEE ============ */}
      <Dialog open={empDlg.open} onOpenChange={(open) => setEmpDlg({ ...empDlg, open })}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto payroll-tab">
          <DialogHeader>
            <DialogTitle className="font-extrabold flex items-center gap-2"><UserPlus className="h-5 w-5 text-blue-600" /> {empDlg.data.id ? "Editar" : "Novo"} Funcionário</DialogTitle>
            <DialogDescription>Cadastro completo do funcionário (dados pessoais, bancários e de trabalho).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="text-xs">Nome Completo *</label>
                <Input value={empDlg.data.name} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, name: e.target.value } })} placeholder="Ex: Maria Silva" data-testid="employee-name" />
              </div>
              <div>
                <label className="text-xs">Função / Cargo</label>
                <Input value={empDlg.data.role} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, role: e.target.value } })} placeholder="Ex: Pedreiro" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="text-xs">CPF</label>
                <Input value={empDlg.data.cpf} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, cpf: e.target.value } })} placeholder="000.000.000-00" data-testid="employee-cpf" />
              </div>
              <div>
                <label className="text-xs">RG</label>
                <Input value={empDlg.data.rg} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, rg: e.target.value } })} />
              </div>
              <div>
                <label className="text-xs">Telefone</label>
                <Input value={empDlg.data.phone} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, phone: e.target.value } })} placeholder="(00) 00000-0000" />
              </div>
            </div>
            <div>
              <label className="text-xs">Endereço</label>
              <Input value={empDlg.data.address} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, address: e.target.value } })} placeholder="Rua, número, bairro, cidade" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="text-xs">Data de Admissão</label>
                <Input type="date" value={empDlg.data.admission_date} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, admission_date: e.target.value } })} />
              </div>
              <div>
                <label className="text-xs">Tipo *</label>
                <Select value={empDlg.data.employment_type} onValueChange={(v) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, employment_type: v } })}>
                  <SelectTrigger data-testid="employee-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensal">Mensal</SelectItem>
                    <SelectItem value="diaria">Diária</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {empDlg.data.employment_type === "mensal" ? (
                <div>
                  <label className="text-xs">Salário Mensal (R$) *</label>
                  <Input type="number" step="0.01" value={empDlg.data.monthly_salary} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, monthly_salary: e.target.value } })} data-testid="employee-salary" />
                </div>
              ) : (
                <div>
                  <label className="text-xs">Valor da Diária (R$) *</label>
                  <Input type="number" step="0.01" value={empDlg.data.daily_rate} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, daily_rate: e.target.value } })} data-testid="employee-daily-rate" />
                </div>
              )}
            </div>
            <div className="border rounded-lg p-3 bg-blue-50">
              <p className="text-xs font-bold mb-2">Dados Bancários / PIX</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input placeholder="Banco" value={empDlg.data.bank_name} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, bank_name: e.target.value } })} />
                <Input placeholder="Agência" value={empDlg.data.bank_agency} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, bank_agency: e.target.value } })} />
                <Input placeholder="Conta" value={empDlg.data.bank_account} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, bank_account: e.target.value } })} />
              </div>
              <div className="mt-2">
                <label className="text-xs">Chave PIX</label>
                <Input value={empDlg.data.pix_key} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, pix_key: e.target.value } })} placeholder="CPF, telefone, e-mail ou aleatória" data-testid="employee-pix" />
              </div>
            </div>
            <div>
              <label className="text-xs">Local / Obra Padrão</label>
              <Select value={empDlg.data.default_project_id || "none"} onValueChange={(v) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, default_project_id: v === "none" ? "" : v } })}>
                <SelectTrigger><SelectValue placeholder="Sem local padrão" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Sem local padrão —</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm font-bold">
              <input type="checkbox" checked={empDlg.data.active !== false} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, active: e.target.checked } })} className="h-4 w-4" />
              Funcionário ativo
            </label>
            <div>
              <label className="text-xs">Observações</label>
              <textarea className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={empDlg.data.notes} onChange={(e) => setEmpDlg({ ...empDlg, data: { ...empDlg.data, notes: e.target.value } })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEmpDlg({ open: false, data: { ...emptyEmp } })}>Cancelar</Button>
              <Button className="bg-blue-600" onClick={saveEmp} data-testid="employee-save-btn">Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ DIALOG: GENERATE BATCH ============ */}
      <Dialog open={genDlg.open} onOpenChange={(open) => setGenDlg({ ...genDlg, open })}>
        <DialogContent className="!max-w-[1100px] w-[calc(100vw-24px)] max-h-[92vh] overflow-y-auto payroll-tab p-4">
          <DialogHeader>
            <DialogTitle className="font-extrabold flex items-center gap-2"><PlayCircle className="h-5 w-5 text-blue-600" /> Gerar Folha de Pagamento</DialogTitle>
            <DialogDescription className="text-xs">Selecione os funcionários, informe dias trabalhados (diaristas) e a medição vinculada.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <label className="text-xs">Competência *</label>
                <Input type="month" value={genDlg.competency_month} onChange={(e) => setGenDlg({ ...genDlg, competency_month: e.target.value })} data-testid="gen-competency" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs">Obra / Local *</label>
                <Select value={genDlg.project_id} onValueChange={(v) => { setGenDlg({ ...genDlg, project_id: v }); if (v !== "SEDE") loadPhases(v); }}>
                  <SelectTrigger data-testid="gen-project"><SelectValue placeholder="Selecione a obra ou SEDE" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SEDE">🏢 SEDE DA EMPRESA (Folha Administrativa)</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs">Vencimento</label>
                <Input type="date" value={genDlg.payment_due_date} onChange={(e) => setGenDlg({ ...genDlg, payment_due_date: e.target.value })} />
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="bg-blue-50 p-2 flex items-center justify-between flex-wrap gap-1">
                <p className="text-sm font-extrabold">Funcionários</p>
                <p className="text-xs whitespace-nowrap">Sel.: <strong>{Object.keys(genDlg.selected).length}</strong> · Total: <strong className="money-total">{fmt(genPreviewTotal)}</strong></p>
              </div>
              <div className="max-h-[46vh] overflow-auto">
                <table className="w-full text-xs table-fixed">
                  <colgroup>
                    <col style={{ width: "30px" }} />
                    <col />
                    <col style={{ width: "60px" }} />
                    <col style={{ width: "90px" }} />
                    <col style={{ width: "50px" }} />
                    <col style={{ width: "130px" }} />
                    <col style={{ width: "70px" }} />
                    <col style={{ width: "70px" }} />
                  </colgroup>
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="p-1.5 text-left">✓</th>
                      <th className="p-1.5 text-left">Nome</th>
                      <th className="p-1.5 text-left">Tipo</th>
                      <th className="p-1.5 text-right">Base</th>
                      <th className="p-1.5 text-center">Dias</th>
                      <th className="p-1.5 text-left">Medição</th>
                      <th className="p-1.5 text-right">Benef.</th>
                      <th className="p-1.5 text-right">Desc.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployeesForGen.map((e) => {
                      const sel = genDlg.selected[e.id];
                      const checked = !!sel;
                      const phases = phasesByProject[genDlg.project_id] || [];
                      const isSede = genDlg.project_id === "SEDE";
                      return (
                        <tr key={e.id} className={`border-b ${checked ? "bg-blue-50" : ""}`} data-testid={`gen-emp-row-${e.id}`}>
                          <td className="p-1.5 align-top">
                            <input type="checkbox" checked={checked} onChange={(ev) => toggleEmpInBatch(e.id, ev.target.checked)} data-testid={`gen-emp-check-${e.id}`} />
                          </td>
                          <td className="p-1.5 align-top overflow-hidden">
                            <p className="font-bold truncate">{e.name}</p>
                            <p className="text-[10px] truncate">{e.role || "—"}</p>
                          </td>
                          <td className="p-1.5 align-top">
                            {e.employment_type === "diaria" ? <Badge variant="outline" className="badge-pill">Diária</Badge> : <Badge className="bg-blue-600 badge-pill">Mensal</Badge>}
                          </td>
                          <td className="p-1.5 text-right align-top whitespace-nowrap">
                            {e.employment_type === "diaria" ? `${fmt(e.daily_rate)}/d` : fmt(e.monthly_salary)}
                          </td>
                          <td className="p-1.5 text-center align-top">
                            {checked && e.employment_type === "diaria" ? (
                              <Input type="number" min="0" className="w-14 h-7 text-xs px-1" value={sel.days_worked} onChange={(ev) => updateEmpBatch(e.id, { days_worked: ev.target.value })} data-testid={`gen-days-${e.id}`} />
                            ) : "—"}
                          </td>
                          <td className="p-1.5 align-top">
                            {checked && !isSede && (
                              <Select value={sel.phase_id || "none"} onValueChange={(v) => updateEmpBatch(e.id, { phase_id: v === "none" ? "" : v })}>
                                <SelectTrigger className="h-7 text-xs w-full px-2" data-testid={`gen-phase-${e.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">—</SelectItem>
                                  {phases.map((ph) => <SelectItem key={ph.id} value={ph.id}>{ph.phase_name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            )}
                            {checked && isSede && <span className="text-xs text-gray-500">— (Sede)</span>}
                          </td>
                          <td className="p-1.5 text-right align-top">
                            {checked && (
                              <Input type="number" step="0.01" className="w-full h-7 text-xs px-1" value={sel.benefits} onChange={(ev) => updateEmpBatch(e.id, { benefits: ev.target.value })} />
                            )}
                          </td>
                          <td className="p-1.5 text-right align-top">
                            {checked && (
                              <Input type="number" step="0.01" className="w-full h-7 text-xs px-1" value={sel.deductions} onChange={(ev) => updateEmpBatch(e.id, { deductions: ev.target.value })} />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <label className="text-xs">Observações</label>
              <textarea className="w-full min-h-[50px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={genDlg.notes} onChange={(e) => setGenDlg({ ...genDlg, notes: e.target.value })} />
            </div>

            <div className="flex justify-end gap-2 sticky bottom-0 bg-white pt-2 border-t">
              <Button variant="outline" onClick={() => setGenDlg({ ...genDlg, open: false })}>Cancelar</Button>
              <Button className="bg-blue-600" onClick={submitGen} data-testid="gen-submit-btn">
                <PlayCircle className="h-4 w-4 mr-1" /> Gerar Folha
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ DIALOG: VIEW BATCH ============ */}
      <Dialog open={viewDlg.open} onOpenChange={(open) => setViewDlg({ ...viewDlg, open })}>
        <DialogContent className="!max-w-[1200px] w-[calc(100vw-24px)] max-h-[92vh] overflow-y-auto payroll-tab p-4">
          <DialogHeader>
            <DialogTitle className="font-extrabold flex items-center gap-2"><ClipboardList className="h-5 w-5 text-blue-600" /> Detalhe da Folha</DialogTitle>
          </DialogHeader>
          {viewDlg.batch && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div><p className="text-xs">Competência</p><p className="font-bold">{monthLabel(viewDlg.batch.competency_month)}</p></div>
                <div><p className="text-xs">Obra</p><p className="font-bold">{viewDlg.batch.project_name || "—"}</p></div>
                <div><p className="text-xs">Total</p><p className="font-extrabold money-out">{fmt(viewDlg.batch.total_amount)}</p></div>
                <div><p className="text-xs">Status</p>{statusBadge(viewDlg.batch.status)}</div>
                <div><p className="text-xs">Vencimento</p><p>{fmtDate(viewDlg.batch.payment_due_date)}</p></div>
                <div><p className="text-xs">Programado p/ pagto</p><p>{fmtDate(viewDlg.batch.payment_scheduled_date)}</p></div>
                <div><p className="text-xs">Pago em</p><p>{fmtDate(viewDlg.batch.paid_at)}</p></div>
                <div><p className="text-xs">Conta</p><p>{viewDlg.batch.bank_account_name || "—"}</p></div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs table-fixed">
                    <colgroup>
                      <col />
                      <col style={{ width: "100px" }} />
                      <col style={{ width: "60px" }} />
                      <col style={{ width: "45px" }} />
                      <col style={{ width: "100px" }} />
                      <col style={{ width: "85px" }} />
                      <col style={{ width: "85px" }} />
                      <col style={{ width: "105px" }} />
                      <col style={{ width: "140px" }} />
                    </colgroup>
                    <thead className="bg-blue-50">
                      <tr className="border-b">
                        <th className="text-left p-1.5">Funcionário</th>
                        <th className="text-left p-1.5">Cargo</th>
                        <th className="text-left p-1.5">Tipo</th>
                        <th className="text-center p-1.5">Dias</th>
                        <th className="text-right p-1.5">Base</th>
                        <th className="text-right p-1.5">Benef.</th>
                        <th className="text-right p-1.5">Desc.</th>
                        <th className="text-right p-1.5">Líquido</th>
                        <th className="text-left p-1.5">Medição</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewDlg.batch.items || []).map((it) => (
                        <tr key={it.id} className="border-b">
                          <td className="p-1.5 font-bold truncate" title={it.employee_name}>{it.employee_name}</td>
                          <td className="p-1.5 truncate" title={it.employee_role || ""}>{it.employee_role || "—"}</td>
                          <td className="p-1.5">{it.employment_type === "diaria" ? "Diária" : "Mensal"}</td>
                          <td className="p-1.5 text-center">{it.days_worked || "—"}</td>
                          <td className="p-1.5 text-right whitespace-nowrap">{fmt(it.base_value)}</td>
                          <td className="p-1.5 text-right whitespace-nowrap">{fmt(it.benefits)}</td>
                          <td className="p-1.5 text-right whitespace-nowrap">{fmt(it.deductions)}</td>
                          <td className="p-1.5 text-right font-extrabold money-out whitespace-nowrap">{fmt(it.net_value)}</td>
                          <td className="p-1.5 truncate" title={it.phase_name || ""}>{it.phase_name || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                      <tr>
                        <td colSpan={7} className="p-2 text-right font-extrabold">Total Líquido</td>
                        <td className="p-2 text-right font-extrabold money-out whitespace-nowrap">{fmt(viewDlg.batch.total_amount)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-2 sticky bottom-0 bg-white pt-2 border-t">
                <Button variant="outline" onClick={() => setViewDlg({ open: false, batch: null })}>Fechar</Button>
                <Button variant="outline" onClick={() => window.print()}>Imprimir</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ DIALOG: PAY BATCH ============ */}
      <Dialog open={payDlg.open} onOpenChange={(open) => setPayDlg({ ...payDlg, open })}>
        <DialogContent className="max-w-md w-[95vw] payroll-tab">
          <DialogHeader>
            <DialogTitle className="font-extrabold flex items-center gap-2"><DollarSign className="h-5 w-5 text-green-600" /> Pagar Folha</DialogTitle>
            <DialogDescription>O valor total será debitado da conta selecionada.</DialogDescription>
          </DialogHeader>
          {payDlg.batch && (
            <div className="space-y-3">
              <div className="bg-gray-50 border rounded-lg p-3">
                <p className="text-sm">Folha {monthLabel(payDlg.batch.competency_month)} · {payDlg.batch.project_name}</p>
                <p className="text-lg font-extrabold money-out">{fmt(payDlg.batch.total_amount)}</p>
                <p className="text-xs">{payDlg.batch.items_count} funcionário(s)</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs">Conta de Origem *</label>
                  <Select value={payDlg.bank_account_id} onValueChange={(v) => setPayDlg({ ...payDlg, bank_account_id: v })}>
                    <SelectTrigger data-testid="payroll-pay-bank"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}{b.bank_name ? ` — ${b.bank_name}` : ""} · {fmt(b.current_balance)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs">Data</label>
                  <Input type="date" value={payDlg.payment_date} onChange={(e) => setPayDlg({ ...payDlg, payment_date: e.target.value })} />
                </div>
              </div>
              {payDlg.bank_account_id && (() => {
                const b = bankAccounts.find((x) => x.id === payDlg.bank_account_id);
                const remaining = (b?.current_balance || 0) - (payDlg.batch?.total_amount || 0);
                return (
                  <p className={`text-xs ${remaining < 0 ? "text-red-600" : ""}`}>
                    Saldo após pagamento: <strong>{fmt(remaining)}</strong>{remaining < 0 && " ⚠️ negativo"}
                  </p>
                );
              })()}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPayDlg({ open: false, batch: null, bank_account_id: "", payment_date: "" })}>Cancelar</Button>
                <Button className="bg-green-500 hover:bg-green-600" onClick={confirmPayBatch} data-testid="payroll-pay-confirm-btn">Confirmar Pagamento</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ DIALOG: APPROVE BATCH (choose medição) ============ */}
      <Dialog open={approveDlg.open} onOpenChange={(open) => setApproveDlg({ ...approveDlg, open })}>
        <DialogContent className="max-w-md w-[95vw] payroll-tab">
          <DialogHeader>
            <DialogTitle className="font-extrabold flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-cyan-600" /> Aprovar Folha</DialogTitle>
            <DialogDescription>
              Selecione a <b>Medição</b> desta obra para lançar o custo em "Materiais e Serviços Utilizados".
            </DialogDescription>
          </DialogHeader>
          {approveDlg.batch && (
            <div className="space-y-3">
              <div className="bg-gray-50 border rounded-lg p-3 text-sm">
                <p><b>Competência:</b> {monthLabel(approveDlg.batch.competency_month)}</p>
                <p><b>Obra/Local:</b> {approveDlg.batch.project_name}</p>
                <p><b>Total:</b> <span className="money-out font-extrabold">{fmt(approveDlg.batch.total_amount)}</span> · {approveDlg.batch.items_count} func.</p>
              </div>
              {approveDlg.batch.project_id === "SEDE" ? (
                <p className="text-xs bg-blue-50 border border-blue-200 rounded p-2">
                  🏢 Folha <b>administrativa</b> da Sede — não será lançada em "Materiais e Serviços" de nenhuma obra.
                </p>
              ) : (
                <div>
                  <label className="text-xs">Medição *</label>
                  <Select value={approveDlg.phase_id} onValueChange={(v) => setApproveDlg({ ...approveDlg, phase_id: v })}>
                    <SelectTrigger data-testid="payroll-approve-phase"><SelectValue placeholder="Selecione a medição" /></SelectTrigger>
                    <SelectContent>
                      {(phasesByProject[approveDlg.batch.project_id] || []).map((ph) => (
                        <SelectItem key={ph.id} value={ph.id}>{ph.phase_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setApproveDlg({ open: false, batch: null, phase_id: "" })}>Cancelar</Button>
                <Button className="bg-cyan-500 hover:bg-cyan-600" onClick={confirmApprove} data-testid="payroll-approve-confirm">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Aprovar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ DIALOG: SCHEDULE BATCH ============ */}
      <Dialog open={schedDlg.open} onOpenChange={(open) => setSchedDlg({ ...schedDlg, open })}>
        <DialogContent className="max-w-sm w-[95vw] payroll-tab">
          <DialogHeader>
            <DialogTitle className="font-extrabold flex items-center gap-2"><CalendarClock className="h-5 w-5 text-blue-600" /> Programar Pagamento</DialogTitle>
            <DialogDescription>Defina a data prevista para pagar esta folha.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs">Data programada *</label>
              <Input type="date" value={schedDlg.date} onChange={(e) => setSchedDlg({ ...schedDlg, date: e.target.value })} data-testid="payroll-schedule-date" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSchedDlg({ open: false, batch: null, date: "" })}>Cancelar</Button>
              <Button className="bg-blue-600" onClick={confirmSchedule} data-testid="payroll-schedule-confirm">
                <Send className="h-3 w-3 mr-1" /> Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
